import { cn } from '../ui/utils';
import { formatCurrency, formatCompactNumber } from '../../lib/utils/format';
import { memo } from 'react';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import type { Currency } from '../../types/currency';

interface ValueDisplayProps {
  /**
   * Numeric value to display
   */
  value: number;
  /**
   * Display format
   * @default 'currency'
   */
  format?: 'currency' | 'compact' | 'number' | 'percentage';
  /**
   * Currency code (for currency format)
   * If not provided, uses the current company's default currency
   */
  currency?: Currency;
  /**
   * Number of decimal places (for number/percentage format)
   * @default 2
   */
  decimals?: number;
  /**
   * Apply color based on value (positive = green, negative = red)
   * @default false
   */
  colorize?: boolean;
  /**
   * Size variant
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /**
   * Font weight
   * @default 'semibold'
   */
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  /**
   * Animate value change
   * @default false
   */
  animate?: boolean;
  className?: string;
  /**
   * Test ID for testing
   * @default 'value-display'
   */
  testId?: string;
}

const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
};

const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

/**
 * ValueDisplay - Formatted value display with styling
 * 
 * Displays numeric values with automatic formatting and optional colorization.
 * Supports currency, compact numbers, percentages.
 * Automatically uses the company's default currency for formatting.
 * 
 * @example
 * ```tsx
 * // Currency (uses company's default currency)
 * <ValueDisplay value={15000} format="currency" colorize />
 * 
 * // Currency with specific currency
 * <ValueDisplay value={15000} format="currency" currency="USD" />
 * 
 * // Compact
 * <ValueDisplay value={2500000} format="compact" size="xl" />
 * 
 * // Percentage
 * <ValueDisplay value={85.5} format="percentage" />
 * ```
 */
export const ValueDisplay = memo(function ValueDisplay({
  value,
  format = 'currency',
  currency,
  decimals = 2,
  colorize = false,
  size = 'md',
  weight = 'semibold',
  animate = false,
  className,
  testId = 'value-display',
}: ValueDisplayProps) {
  // Use company's default currency if not explicitly provided
  const companyCurrency = useCompanyCurrency();
  const effectiveCurrency = currency || companyCurrency;
  const formatValue = (): string => {
    switch (format) {
      case 'currency':
        return formatCurrency(value, effectiveCurrency);
      case 'compact':
        return formatCompactNumber(value, decimals);
      case 'percentage':
        return `${value.toFixed(decimals)}%`;
      case 'number':
        return value.toLocaleString('pt-BR', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      default:
        return value.toString();
    }
  };

  const colorClass = colorize
    ? value > 0
      ? 'text-success-600 dark:text-success-400'
      : value < 0
      ? 'text-danger-600 dark:text-danger-400'
      : 'text-neutral-600 dark:text-neutral-400'
    : '';

  return (
    <span
      className={cn(
        'tabular-nums',
        sizeClasses[size],
        weightClasses[weight],
        colorClass,
        animate && 'transition-all duration-300',
        className
      )}
      data-testid={testId}
      aria-label={`Valor: ${formatValue()}`}
    >
      {formatValue()}
    </span>
  );
});
